import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppButton, AppCard, AppField, AppInput, AppModal } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type Manufacturer = {
  id: number;
  name: string;
  code: string;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  device_count: number;
};

type DeviceType = {
  id: number;
  name: string;
  code: string;
  description: string | null;
  capacity: string | null;
  default_price: number;
  subsidy_percent: number;
  subsidized_price: number;
};

function CreateManufacturerModal({ open, onClose, onSuccess }: {
  open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [form, setForm] = useState({ name: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) { setForm({ name: "", code: "" }); setErr(""); } }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) { setErr("Name and code are required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/manufacturers", { name: form.name.trim(), code: form.code.trim().toUpperCase() });
      onSuccess(); onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create manufacturer");
    } finally { setBusy(false); }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Add Manufacturer">
      <form onSubmit={submit}>
        <AppField label="Manufacturer Name">
          <AppInput id="mfr-name" type="text" placeholder="e.g. SunKing" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} autoFocus />
        </AppField>
        <AppField label="Code">
          <AppInput id="mfr-code" type="text" placeholder="e.g. SUNK" value={form.code} onChange={(e) => set("code", e.target.value)} disabled={busy} />
        </AppField>
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Adding…</> : "Add Manufacturer"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

function CreateDeviceTypeModal({ open, onClose, onSuccess }: {
  open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [form, setForm] = useState({ name: "", code: "", capacity: "", default_price: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) { setForm({ name: "", code: "", capacity: "", default_price: "" }); setErr(""); } }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) { setErr("Name and code are required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/device-types", {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        capacity: form.capacity || undefined,
        default_price: form.default_price ? parseFloat(form.default_price) : undefined,
      });
      onSuccess(); onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create device type");
    } finally { setBusy(false); }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Add Device Type">
      <form onSubmit={submit}>
        <AppField label="Device Type Name">
          <AppInput id="dt-name" type="text" placeholder="e.g. Solar Home System 60W" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} autoFocus />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Code">
            <AppInput id="dt-code" type="text" placeholder="e.g. SHS60" value={form.code} onChange={(e) => set("code", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Capacity (optional)">
            <AppInput id="dt-capacity" type="text" placeholder="e.g. 60W" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Default Price (optional, TZS)">
          <AppInput id="dt-price" type="number" min={0} value={form.default_price} onChange={(e) => set("default_price", e.target.value)} disabled={busy} />
        </AppField>
        {err && <span className="error" role="alert">{err}</span>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Adding…</> : "Add Device Type"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function ManufacturerCatalog() {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [mfrModalOpen, setMfrModalOpen] = useState(false);
  const [dtModalOpen, setDtModalOpen] = useState(false);

  const load = async () => {
    setPageState("loading");
    try {
      const [mv, dv] = await Promise.all([
        api.get<{ manufacturers: Manufacturer[] }>("/manufacturers"),
        api.get<{ device_types: DeviceType[] }>("/device-types"),
      ]);
      setManufacturers(mv.manufacturers ?? []);
      setDeviceTypes(dv.device_types ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load catalogue");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, []);

  const mfrColumns: ColumnDef<Manufacturer, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "code", header: "Code" },
    { id: "contact", header: "Contact", accessorFn: (r) => r.contact_person ?? r.contact_email ?? r.contact_phone ?? "—" },
    { accessorKey: "device_count", header: "Devices" },
  ];

  const dtColumns: ColumnDef<DeviceType, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "code", header: "Code" },
    { id: "capacity", header: "Capacity", accessorFn: (r) => r.capacity ?? "—" },
    { id: "default_price", header: "Default Price (TZS)", accessorFn: (r) => r.default_price.toLocaleString() },
    { id: "subsidy", header: "Subsidy", accessorFn: (r) => r.subsidy_percent > 0 ? `${r.subsidy_percent}%` : "—" },
    { id: "subsidized_price", header: "Subsidized Price (TZS)", accessorFn: (r) => r.subsidized_price.toLocaleString() },
  ];

  return (
    <div>
      <CreateManufacturerModal open={mfrModalOpen} onClose={() => setMfrModalOpen(false)} onSuccess={load} />
      <CreateDeviceTypeModal open={dtModalOpen} onClose={() => setDtModalOpen(false)} onSuccess={load} />

      <PageHeader
        title="Manufacturers & Device Types"
        subtitle="Reference data for device registration and bulk import."
      />

      <PageState status={pageState} error={pageError} onRetry={load}>
        <AppCard title="Manufacturers">
          <div className="pg-table-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{manufacturers.length} manufacturer{manufacturers.length !== 1 ? "s" : ""}</span>
            <AppButton type="button" onClick={() => setMfrModalOpen(true)}>Add Manufacturer</AppButton>
          </div>
          {manufacturers.length === 0 ? (
            <PageState status="empty" emptyTitle="No manufacturers" emptyHint="Add a manufacturer to register devices." />
          ) : (
            <AppDataTable data={manufacturers} columns={mfrColumns} />
          )}
        </AppCard>

        <AppCard title="Device Types" style={{ marginTop: 16 }}>
          <div className="pg-table-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{deviceTypes.length} device type{deviceTypes.length !== 1 ? "s" : ""}</span>
            <AppButton type="button" onClick={() => setDtModalOpen(true)}>Add Device Type</AppButton>
          </div>
          {deviceTypes.length === 0 ? (
            <PageState status="empty" emptyTitle="No device types" emptyHint="Add a device type to classify your fleet." />
          ) : (
            <AppDataTable data={deviceTypes} columns={dtColumns} />
          )}
        </AppCard>
      </PageState>
    </div>
  );
}
